/*
 * THE PROFILE TAB'S LAST MILE: how frame 1f lays the registry out, and how each
 * pencil writes.
 *
 * record/profileModel.mjs decides WHAT is known (the registry, the source chips,
 * the counts). This module decides two things the frame adds on top:
 *
 *   - WHICH GROUPS READ AS ONE LINE. An empty group is one line ("LinkedIn ads ·
 *     no ad account linked"), and so is a group whose rows only say what its line
 *     already says: "How they arrived · intro email from Dani Avitz, Sep 15", with
 *     the intro's own chip, rather than an "Arrived Sep 15" row and an "Intro" row
 *     under a header. "Other · would score 33 today" wears "Computed", because it
 *     is. A search shows the rows themselves, so a match is never hidden in a line.
 *
 *   - HOW EACH PENCIL EDITS AND WRITES (editSpec, editWrite). Each field has one
 *     write path and only one: a column through PATCH (contacts or companies), a
 *     pipeline field through the same PATCH (monthly_value, won_at, recycle_until
 *     ride updateCompanyPipeline), a property through properties_set (PR #154's
 *     allowlists in src/db/recordFields.js, which also parse "$4M" and "2015"
 *     server side), or another address through POST /contacts/:id/emails. The
 *     input starts from the STORED value, never the display: the fee row reads
 *     "$3,500/mo base · 15% …" joined, but what is edited is the column's own text.
 *
 * Pure, so test/record-profile-view.test.js pins it without a browser.
 */
import { fullName, moneyShort, enumWords } from "./format.mjs";

const props = (o) => (o && o.properties && typeof o.properties === "object" ? o.properties : {});
const blank = (v) => v === null || v === undefined || (typeof v === "string" && !v.trim());

/* ------------------------------------------------------------ the groups */

// The rows "How they arrived" is made of when its line can say them all.
const ARRIVAL_ROWS = new Set(["arrived", "intro", "first_form"]);

/**
 * The model's groups as the tab draws them: each gains `chip` (the one-line
 * group's source chip, or null) and `oneLine` is widened to the groups whose
 * rows the line already says. `query` is the search text.
 */
export function profileGroups(groups, { query = "" } = {}) {
  const searching = !!String(query || "").trim();
  return (groups || []).map((g) => {
    if (g.oneLine) {
      // An empty group with no line of its own still says it is empty, rather than
      // standing as a bare title that reads like a header with its rows missing.
      return { ...g, summary: g.summary || "nothing recorded yet", chip: g.id === "other" && g.summary ? { text: "Computed" } : null };
    }
    if (!searching && g.id === "arrived" && g.summary && g.rows.every((r) => ARRIVAL_ROWS.has(r.key))) {
      const said = g.rows.find((r) => r.key === "intro" || r.key === "first_form");
      return { ...g, oneLine: true, chip: said && said.source ? said.source : null };
    }
    return { ...g, chip: null };
  });
}

/* ------------------------------------------------------------ editing */

export const LIFECYCLE_OPTIONS = [
  { value: "subscriber", label: "subscriber" },
  { value: "lead", label: "lead" },
  { value: "marketingqualifiedlead", label: "MQL" },
  { value: "salesqualifiedlead", label: "SQL" },
  { value: "opportunity", label: "opportunity" },
  { value: "customer", label: "customer" },
  { value: "evangelist", label: "evangelist" },
  { value: "other", label: "other" },
];
export const KIND_OPTIONS = [
  { value: "lead", label: "Lead" },
  { value: "associated", label: "Associated" },
];
export const SOURCE_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "referral", label: "Referral" },
  { value: "organic", label: "Organic" },
];

const ymd = (ts) => {
  if (!ts) return "";
  const s = String(ts);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!Number.isFinite(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const moneyText = (v) => (blank(v) ? "" : Number.isFinite(Number(v)) ? moneyShort(Number(v)) : String(v));

/**
 * "$10K", "10,000", "2.5m", "10000" → 10000. Null for blank (clears the field),
 * NaN for anything that is not an amount, so the caller can say so beside the box.
 */
export function parseMoney(text) {
  if (blank(text)) return null;
  const raw = String(text).trim().toLowerCase().replace(/[$€£₪,\s]/g, "").replace(/\/mo(nth)?$/, "");
  const m = raw.match(/^(\d+(?:\.\d+)?)([km])?$/);
  if (!m) return NaN;
  const scale = { k: 1e3, m: 1e6 }[m[2]] || 1;
  return Math.round(Number(m[1]) * scale);
}

/**
 * What the pencil opens, or null where nothing can write the field:
 *   { input: 'text' | 'textarea' | 'select' | 'date', initial, options?, placeholder?,
 *     hint?, add? }
 * `facets.industry` is the stored industry values (the Leads page's facets), so an
 * industry edit picks a value the column already holds instead of inventing one.
 */
export function editSpec(row, { contact = null, company = null, facets = null } = {}) {
  const e = row && row.edit;
  if (!e) return null;
  const c = contact || {};
  const co = company || {};
  const hint = row.hint || null;
  if (e.emails) return { input: "text", initial: "", placeholder: "another@address.com", add: true, hint: "Kept as another address for the same person; their main email stays." };
  if (e.target === "contact" && e.field) {
    switch (e.field) {
      case "name":
        return { input: "text", initial: fullName({ first_name: c.first_name, last_name: c.last_name }) || "", placeholder: "First and last name" };
      case "kind":
        return { input: "select", initial: c.kind === "associated" ? "associated" : "lead", options: KIND_OPTIONS };
      case "lifecycle_stage":
        return { input: "select", initial: c.lifecycle_stage || "lead", options: LIFECYCLE_OPTIONS };
      case "lead_source":
        return { input: "select", initial: c.lead_source || "", options: SOURCE_OPTIONS };
      default:
        return { input: "text", initial: blank(c[e.field]) ? "" : String(c[e.field]), hint };
    }
  }
  if (e.target === "contact" && e.prop) {
    if (e.prop === "hs_timezone") {
      const z = c.local_zone;
      return { input: "text", initial: props(c).hs_timezone || (z && z.from === "contact" ? z.tz : "") || "", placeholder: "America/Chicago", hint: "Their own zone, which every time on the record is read in." };
    }
    if (e.prop === "referred_by") return { input: "text", initial: props(c).referred_by || c.referrer_name || "", placeholder: "Who introduced them" };
    return { input: "text", initial: blank(props(c)[e.prop]) ? "" : String(props(c)[e.prop]) };
  }
  if (e.target === "company" && e.field) {
    switch (e.field) {
      case "primary_contact_id": {
        const people = (co.contacts || []).filter((p) => p && p.kind !== "junk");
        if (people.length < 2) return null;
        return { input: "select", initial: co.primary_contact_id ? String(co.primary_contact_id) : "", options: people.map((p) => ({ value: String(p.id), label: fullName(p) || p.email || "Someone" })) };
      }
      case "industry": {
        const values = [...new Set([...(facets && facets.industry ? facets.industry : []), ...(co.industry ? [co.industry] : [])])].sort();
        if (!values.length) return { input: "text", initial: co.industry || "" };
        return { input: "select", initial: co.industry || "", options: [{ value: "", label: "Not set" }, ...values.map((v) => ({ value: v, label: enumWords(v) }))] };
      }
      case "monthly_value":
        return { input: "text", initial: moneyText(co.monthly_value), placeholder: "$10K", hint: "Their monthly LinkedIn budget while a prospect; on a client this field is our retainer." };
      case "won_at":
        return co.status === "client" ? { input: "date", initial: ymd(co.won_at) } : null;
      case "recycle_until":
        // Empty is allowed: it stops the reminder ahead of the day, which used to
        // be possible only on the wake-up day itself, from Next.
        return co.status === "recycled" ? { input: "date", initial: ymd(co.recycle_until), hint: "The day it comes back to the pipeline. Empty stops the reminder; it still comes back when they write." } : null;
      case "audited_at":
        return { input: "date", initial: ymd(co.audited_at), hint: "When we audited their LinkedIn Ads account. Empty takes the flag off." };
      case "audit_notes":
        return { input: "textarea", initial: co.audit_notes || "", placeholder: "What the audit found" };
      case "signed_fee_structure":
        return { input: "textarea", initial: co.signed_fee_structure || "", placeholder: "One line per part: $3,500/mo base, 15% of spend $10K–$50K…" };
      case "signed_terms_note":
        return { input: "textarea", initial: co.signed_terms_note || "", placeholder: "What was negotiated" };
      case "name":
        return { input: "text", initial: co.name || "" };
      default:
        return { input: "text", initial: blank(co[e.field]) ? "" : String(co[e.field]), hint };
    }
  }
  if (e.target === "company" && e.prop) {
    const p = props(co);
    switch (e.prop) {
      case "company_description":
        return { input: "textarea", initial: p.company_description || p.description || "" };
      case "total_funding":
        return { input: "text", initial: moneyText(!blank(p.total_funding) ? p.total_funding : p.total_money_raised), placeholder: "$4M", hint };
      case "founded_year":
        return { input: "text", initial: blank(p.founded_year) ? "" : String(p.founded_year), placeholder: "2015" };
      case "company_website":
        return { input: "text", initial: p.company_website || co.domain || "", placeholder: "pluto.security" };
      case "timezone":
        return { input: "text", initial: p.timezone || "", placeholder: "America/New_York" };
      default:
        return { input: "text", initial: blank(p[e.prop]) ? "" : String(p[e.prop]) };
    }
  }
  return null;
}

/**
 * The write for a saved edit:
 *   { via: 'contact', id, body }      PATCH /contacts/:id
 *   { via: 'company', body }          PATCH /companies/:id
 *   { via: 'props', target, id, props } properties_set on either
 *   { via: 'email', id, email }       POST /contacts/:id/emails
 *   { error }                         said beside the box, nothing sent
 * An emptied box clears the field (null), except where a value is required.
 */
export function editWrite(row, value, { contact = null, company = null } = {}) {
  const e = row && row.edit;
  if (!e) return { error: "This field can't be edited here." };
  const v = typeof value === "string" ? value.trim() : value;
  const empty = blank(v);
  if (e.emails) {
    if (empty) return { error: "Type the other address first." };
    return { via: "email", id: contact && contact.id, email: v };
  }
  if (e.target === "contact" && e.prop) return { via: "props", target: "contact", id: contact && contact.id, props: { [e.prop]: empty ? null : v } };
  if (e.target === "company" && e.prop) return { via: "props", target: "company", id: company && company.id, props: { [e.prop]: empty ? null : v } };
  if (e.target === "contact") {
    if (e.field === "name") {
      if (empty) return { error: "A person needs a name." };
      const [first, ...rest] = String(v).split(/\s+/);
      return { via: "contact", id: contact && contact.id, body: { first_name: first, last_name: rest.length ? rest.join(" ") : null } };
    }
    if (e.field === "email" && empty) return { error: "Their main email can't be blank. Add another address instead." };
    if (e.field === "kind" && empty) return { error: "Pick lead or associated." };
    if (e.field === "lifecycle_stage" && empty) return { error: "Pick a stage." };
    return { via: "contact", id: contact && contact.id, body: { [e.field]: empty ? null : v } };
  }
  if (e.target === "company") {
    if (e.field === "name" && empty) return { error: "A company needs a name." };
    if (e.field === "monthly_value") {
      const n = parseMoney(v);
      if (Number.isNaN(n)) return { error: "That has to be an amount, like 10000 or $10K." };
      return { via: "company", body: { monthly_value: n } };
    }
    if (e.field === "won_at" && empty) return { error: "Pick a date." };
    return { via: "company", body: { [e.field]: empty ? null : v } };
  }
  return { error: "This field can't be edited here." };
}
